import React, { useEffect, useState, useCallback } from "react";
import { Trophy } from "lucide-react";
import { api } from "../../lib/api";
import { modelBrand } from "../../lib/modelBrand";
import { useLang } from "../../contexts/LanguageContext";

/**
 * Compact leaderboard shown on the hero. Hidden until the user has cast at least one vote.
 * `refreshKey` — bump to force a re-fetch (e.g. after a new pin).
 */
export default function ModelLeaderboard({ refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const { t } = useLang();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/stats/model-wins?scope=me");
      setData(data);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  if (!data || data.total_votes === 0) return null;

  const topWins = data.leaderboard[0]?.wins || 1;

  return (
    <div
      className="mt-14 mx-auto max-w-md p-5 rounded-lg border border-white/10 bg-white/[0.02]"
      data-testid="model-leaderboard"
    >
      <div className="flex items-center gap-2 mb-4">
        <Trophy className="w-3.5 h-3.5 text-[#F5C542]" />
        <div className="label-eyebrow">{t("leaderboard.title")}</div>
        <div className="label-eyebrow text-white/40 ml-auto" data-testid="leaderboard-total">
          {t("leaderboard.pins", { count: data.total_votes, plural: data.total_votes === 1 ? "" : "s" })}
        </div>
      </div>
      <ul className="space-y-2.5 text-left">
        {data.leaderboard.map((row) => {
          const { accentClass } = modelBrand(row.id);
          const barWidth = topWins > 0 ? Math.max(4, (row.wins / topWins) * 100) : 0;
          return (
            <li
              key={row.id}
              className={`${accentClass} flex items-center gap-3`}
              data-testid={`leaderboard-row-${row.id}`}
            >
              <span className="w-2 h-2 rounded-full accent-dot flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-[12px] text-white/85 truncate">{row.label}</span>
                  <span className="font-mono-p text-[11px] text-white/50">
                    {(row.win_rate * 100).toFixed(0)}%
                    <span className="text-white/30"> · {row.wins}</span>
                  </span>
                </div>
                <div className="mt-1 h-[3px] rounded-full bg-white/5 overflow-hidden">
                  <div
                    className="h-full rounded-full accent-dot"
                    style={{ width: `${barWidth}%`, transition: "width 400ms ease-out" }}
                  />
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
